import { lazy, Suspense } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { importWithChunkReload } from '@/utils/lazyRetry';
import { DebugPanel } from '../../components/debug/DebugPanel';
import { useViewSettings } from '../../contexts/ViewSettingsContext';
import { usePermissions } from '../../hooks/usePermissions';
import { isDebugPanelEnabled } from '../../utils/debugUtils';

// Lazy so the agent-chat stack (react-markdown/micromark/mdast) stays out of
// the entry chunk; GlobalChrome mounts on every authenticated page.
const FloatingAgentChat = lazy(() =>
  importWithChunkReload(() =>
    import('../../components/ui/floating-agent-chat').then((module) => ({
      default: module.FloatingAgentChat,
    }))
  )
);

type GlobalChromeProps = {
  userRole: string | null;
  shouldHideFloatingChat?: boolean;
};

export const GlobalChrome = ({ userRole, shouldHideFloatingChat = false }: GlobalChromeProps) => {
  const { viewSettings, updateViewSetting } = useViewSettings();
  const { hasPermission } = usePermissions();
  const { pathname } = useLocation();
  const canUsePlatformChat = hasPermission('platform_chat', 'read');
  const isTelosPage = Boolean(matchPath({ path: '/platform-chat', end: false }, pathname));

  return (
    <div data-testid="globalChrome_root" style={{ display: 'contents' }}>
      {canUsePlatformChat &&
        userRole !== 'CLIENT' &&
        userRole !== 'CORPORATE_ADMIN' &&
        !isTelosPage &&
        !shouldHideFloatingChat && (
          <ErrorBoundary
            fallback={<span role="alert">Chat unavailable. Please reload to try again.</span>}
          >
            <Suspense fallback={null}>
              <FloatingAgentChat />
            </Suspense>
          </ErrorBoundary>
        )}
      {isDebugPanelEnabled() && (
        <DebugPanel
          isOpen={viewSettings.debugPanel}
          onClose={() => updateViewSetting('debugPanel', false)}
        />
      )}
    </div>
  );
};
